import { useCallback, useState } from 'react';
import axios from 'axios';

import { useSysconfig } from '~/components/SystemConfiguration/SystemConfigurationScreen';
import type {
  GenericResponse,
  MockPolicyServerStatusModel,
  MockPolicyServerToggleResult,
} from '~/server_shared/openapi/data-contracts';

export const useMockPolicyServer = () => {
  const { organization } = useSysconfig() as { organization: { id: string } };
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<MockPolicyServerStatusModel | null>(null);

  const baseUrl = `/sysconfig/api/v1/organizations/${organization.id}/mock_policy_server`;

  // Get mock policy server status
  const getStatus = useCallback(async (): Promise<MockPolicyServerStatusModel> => {
    setLoading(true);
    setError(null);

    try {
      const response = await axios.get<MockPolicyServerStatusModel>(`${baseUrl}/status`);
      const statusData: MockPolicyServerStatusModel = response.data;
      setStatus(statusData);
      return statusData;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to get status';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [baseUrl]);

  // Enable mock policy server
  const enable = useCallback(async (): Promise<MockPolicyServerToggleResult> => {
    setLoading(true);
    setError(null);

    try {
      const response = await axios.post<MockPolicyServerToggleResult | GenericResponse>(`${baseUrl}/enable`);

      // Check if response is an error (400 status with GenericResponse format)
      if ('success' in response.data && !response.data.success) {
        const errorResponse = response.data as GenericResponse;
        throw new Error(errorResponse.message);
      }

      const result = response.data as MockPolicyServerToggleResult;

      // Update local status
      if (status) {
        setStatus({ ...status, enabled: true });
      }

      return result;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to enable mock server';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [baseUrl, status]);

  // Disable mock policy server
  const disable = useCallback(async (): Promise<MockPolicyServerToggleResult> => {
    setLoading(true);
    setError(null);

    try {
      const response = await axios.post<MockPolicyServerToggleResult>(`${baseUrl}/disable`);
      const result: MockPolicyServerToggleResult = response.data;

      // Update local status
      if (status) {
        setStatus({ ...status, enabled: false });
      }

      return result;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to disable mock server';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [baseUrl, status]);

  // Check if mock server is enabled
  const isEnabled = useCallback((): boolean => {
    return status?.enabled || false;
  }, [status]);

  return {
    status,
    loading,
    error,
    getStatus,
    enable,
    disable,
    isEnabled,
  };
};
